import React, { useState } from 'react';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  jobProblems,
  jobsError,
  useSavePostedJob,
  useSuggestTags,
} from '../../hooks/usePostedJobs';
import { dayFromDueAt, dueAtFromDay } from '../forms/formsText';

const INPUT =
  'rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-claret-500 focus:outline-none focus:ring-1 focus:ring-claret-500';
const PRIMARY =
  'rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400 disabled:cursor-not-allowed disabled:opacity-50';
const SECONDARY =
  'rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

export const WORKPLACES = [
  { value: 'kenya', label: 'In Kenya (on site or hybrid)' },
  { value: 'remote_africa', label: 'Remote, open to Africa' },
  { value: 'remote_global', label: 'Remote, worldwide' },
  {
    value: 'remote_restrict',
    label: 'Remote, not open to Kenya',
    note: 'Never sent to graduates.',
  },
];

export const SENIORITIES = [
  { value: 'entry', label: 'Entry level' },
  { value: 'junior', label: 'Junior' },
  { value: 'unknown', label: 'Not specified' },
];

export const FAMILIES = [
  { value: 'software', label: 'Software development' },
  { value: 'data', label: 'Data' },
  { value: 'ai_ml', label: 'AI and machine learning' },
  { value: 'cloud_devops', label: 'Cloud and DevOps' },
  { value: 'cybersecurity', label: 'Cybersecurity' },
  { value: 'it_support', label: 'IT support' },
  { value: 'qa_testing', label: 'QA and testing' },
  { value: 'design', label: 'Design' },
  { value: 'product', label: 'Product' },
];

const MAX_SUMMARY = 160;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/\S+\.\S+$/;

function FieldError({ message }) {
  return message ? (
    <span className="text-[11px] text-red-700">{message}</span>
  ) : null;
}

function SkillChips({ skills, onChange }) {
  const [input, setInput] = useState('');
  const add = () => {
    const parts = input
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => !skills.some((x) => x.toLowerCase() === s.toLowerCase()));
    if (parts.length) onChange([...skills, ...parts]);
    setInput('');
  };
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {skills.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-800"
          >
            {s}
            <button
              type="button"
              aria-label={`Remove ${s}`}
              onClick={() => onChange(skills.filter((x) => x !== s))}
              className="text-gray-400 hover:text-gray-700"
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        aria-label="Add a skill"
        placeholder="Type a skill and press Enter, e.g. SQL"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        className={`${INPUT} w-full`}
      />
    </div>
  );
}

// Add (no job) or edit a job staff found.
export default function PostedJobForm({ job, onDone, onCancel }) {
  const save = useSavePostedJob();
  const suggest = useSuggestTags();
  const [values, setValues] = useState({
    title: job?.title ?? '',
    company: job?.company ?? '',
    location: job?.location ?? '',
    workplace: job?.workplace ?? 'kenya',
    apply_url: job?.apply_url ?? '',
    apply_email: job?.apply_email ?? '',
    description: job?.description ?? '',
    closesDay: dayFromDueAt(job?.closes_at),
    seniority: job?.seniority ?? 'unknown',
    families: job?.families ?? [],
    skills: job?.skills ?? [],
    summary: job?.summary ?? '',
  });
  const [applyBy, setApplyBy] = useState(
    job?.apply_email && !job?.apply_url ? 'email' : 'link'
  );
  const [problems, setProblems] = useState({});
  const [similar, setSimilar] = useState(null);
  const [suggestNote, setSuggestNote] = useState(null);
  const today = new Date(Date.now() + 3 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  const set = (key, value) => {
    setValues((v) => ({ ...v, [key]: value }));
    setProblems((p) => ({ ...p, [key]: undefined }));
  };

  const local = {
    apply_url:
      applyBy === 'link' &&
      values.apply_url &&
      !URL_RE.test(values.apply_url.trim())
        ? 'Use a full link starting with https://'
        : null,
    apply_email:
      applyBy === 'email' &&
      values.apply_email &&
      !EMAIL_RE.test(values.apply_email.trim())
        ? 'Enter an email address.'
        : null,
  };
  const applyValue =
    applyBy === 'link' ? values.apply_url.trim() : values.apply_email.trim();
  const ready =
    values.title.trim() &&
    values.company.trim() &&
    applyValue &&
    !local.apply_url &&
    !local.apply_email &&
    values.summary.length <= MAX_SUMMARY;

  const submitLabel = job ? 'Save changes' : 'Add job';

  const body = () => ({
    title: values.title.trim(),
    company: values.company.trim(),
    location: values.location.trim(),
    workplace: values.workplace,
    apply_url: applyBy === 'link' ? values.apply_url.trim() : '',
    apply_email: applyBy === 'email' ? values.apply_email.trim() : '',
    description: values.description.trim(),
    closes_at: dueAtFromDay(values.closesDay),
    seniority: values.seniority,
    families: values.families,
    skills: values.skills,
    summary: values.summary.trim(),
  });

  const submit = (force = false) =>
    save.mutate(
      { id: job?.id, job: body(), force },
      {
        onSuccess: () => {
          toast.success(
            job
              ? 'Job updated.'
              : 'Job added. It goes in the next weekly digest.'
          );
          onDone();
        },
        onError: (error) => {
          const status = error?.response?.status;
          const data = error?.response?.data;
          if (status === 409 && Array.isArray(data?.similar)) {
            setSimilar(data.similar);
            return;
          }
          setProblems(jobProblems(error));
          toast.error(jobsError(error, "We couldn't save the job."));
        },
      }
    );

  const runSuggest = () =>
    suggest.mutate(
      {
        title: values.title.trim(),
        company: values.company.trim(),
        location: values.location.trim(),
        description: values.description.trim(),
      },
      {
        onSuccess: (s) => {
          setValues((v) => ({
            ...v,
            families: s?.families ?? v.families,
            skills: s?.skills ?? v.skills,
            seniority: s?.seniority || v.seniority,
            workplace: s?.workplace || v.workplace,
            summary: s?.summary ? s.summary.slice(0, MAX_SUMMARY) : v.summary,
          }));
          const notes = [];
          if (s?.senior) {
            notes.push(
              'This reads as a mid-level or senior role. Senior roles aren’t sent to graduates.'
            );
          }
          if (s?.is_tech === false) {
            notes.push(
              'This doesn’t look like a tech role, so few graduates may be matched to it.'
            );
          }
          setSuggestNote({
            tone: notes.length ? 'warn' : 'ok',
            text: notes.length
              ? notes.join(' ')
              : 'Suggestions filled in below. Check them before saving.',
          });
        },
        onError: (error) =>
          setSuggestNote({
            tone: 'warn',
            text:
              error?.response?.status === 503
                ? 'Suggestions aren’t available right now. Fill these in yourself.'
                : jobsError(error, 'We couldn’t get suggestions.'),
          }),
      }
    );

  if (similar) {
    return (
      <div className="space-y-3 text-left">
        <p className="text-sm text-gray-800">
          A similar job from {values.company.trim()} is already listed. Adding
          it again would send graduates the same job twice.
        </p>
        <ul className="m-0 list-none divide-y divide-gray-100 rounded-lg border border-gray-200 p-0">
          {similar.map((s) => (
            <li key={`${s.source}-${s.id}`} className="px-3 py-2 text-sm">
              <span className="text-gray-900">{s.title}</span> · {s.company}
              <span className="ml-2 text-xs text-gray-500">
                {s.source === 'plp' ? 'Added by staff' : 'Collected'}
              </span>
              {s.url && (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-xs text-claret-500 hover:underline"
                >
                  Open
                </a>
              )}
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setSimilar(null)}
            className={SECONDARY}
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => submit(true)}
            disabled={save.isLoading}
            className={PRIMARY}
          >
            {save.isLoading ? 'Adding…' : 'Add anyway'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4 text-left"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) submit(false);
      }}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Job title
          <input
            type="text"
            value={values.title}
            onChange={(e) => set('title', e.target.value)}
            className={INPUT}
          />
          <FieldError message={problems.title} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Company
          <input
            type="text"
            value={values.company}
            onChange={(e) => set('company', e.target.value)}
            className={INPUT}
          />
          <FieldError message={problems.company} />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
          Location
          <input
            type="text"
            placeholder="e.g. Nairobi"
            value={values.location}
            onChange={(e) => set('location', e.target.value)}
            className={INPUT}
          />
          <FieldError message={problems.location} />
        </label>
        <div className="flex flex-col gap-1">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            Closing date (optional)
            <input
              type="date"
              min={job ? undefined : today}
              value={values.closesDay}
              onChange={(e) => set('closesDay', e.target.value)}
              className={`${INPUT} w-48`}
            />
          </label>
          <span className="text-[11px] font-normal text-gray-500">
            End of that day, Kenya time. Without one, it&apos;s sent for 21
            days.
          </span>
          <FieldError message={problems.closes_at} />
        </div>
      </div>

      <fieldset className="space-y-1.5">
        <legend className="text-xs font-medium text-gray-700">Where</legend>
        {WORKPLACES.map((w) => (
          <label
            key={w.value}
            className="flex items-start gap-2 text-sm text-gray-800"
          >
            <input
              type="radio"
              name="workplace"
              className="mt-1"
              checked={values.workplace === w.value}
              onChange={() => set('workplace', w.value)}
            />
            <span>
              {w.label}
              {w.note && (
                <span className="ml-1 text-xs text-amber-700">{w.note}</span>
              )}
            </span>
          </label>
        ))}
        <FieldError message={problems.workplace} />
      </fieldset>

      <div className="space-y-1.5">
        <div className="flex gap-4 text-xs font-medium text-gray-700">
          How to apply:
          {[
            ['link', 'Apply link'],
            ['email', 'Apply by email'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-1.5 font-normal">
              <input
                type="radio"
                name="applyBy"
                checked={applyBy === key}
                onChange={() => setApplyBy(key)}
              />
              {label}
            </label>
          ))}
        </div>
        {applyBy === 'link' ? (
          <input
            type="url"
            aria-label="Apply link"
            placeholder="https://"
            value={values.apply_url}
            onChange={(e) => set('apply_url', e.target.value)}
            className={`${INPUT} w-full`}
          />
        ) : (
          <input
            type="email"
            aria-label="Apply email"
            placeholder="jobs@company.com"
            value={values.apply_email}
            onChange={(e) => set('apply_email', e.target.value)}
            className={`${INPUT} w-full`}
          />
        )}
        <FieldError
          message={
            local.apply_url ||
            local.apply_email ||
            problems.apply_url ||
            problems.apply_email
          }
        />
      </div>

      <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
        Description
        <textarea
          rows={5}
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
          className={INPUT}
        />
        <FieldError message={problems.description} />
      </label>

      <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-600">
            Who it&apos;s for: graduates are matched by job area and skills.
          </p>
          <button
            type="button"
            onClick={runSuggest}
            disabled={!values.title.trim() || suggest.isLoading}
            className={`${SECONDARY} inline-flex items-center gap-1`}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
            {suggest.isLoading ? 'Suggesting…' : 'Suggest from description'}
          </button>
        </div>
        {suggestNote && (
          <p
            className={`text-xs ${
              suggestNote.tone === 'warn' ? 'text-amber-800' : 'text-gray-600'
            }`}
          >
            {suggestNote.text}
          </p>
        )}
        <fieldset className="space-y-1">
          <legend className="text-xs font-medium text-gray-700">
            Job areas
          </legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {FAMILIES.map((f) => (
              <label
                key={f.value}
                className="flex items-center gap-1.5 text-sm text-gray-800"
              >
                <input
                  type="checkbox"
                  checked={values.families.includes(f.value)}
                  onChange={(e) =>
                    set(
                      'families',
                      e.target.checked
                        ? [...values.families, f.value]
                        : values.families.filter((x) => x !== f.value)
                    )
                  }
                />
                {f.label}
              </label>
            ))}
          </div>
          <FieldError message={problems.families} />
        </fieldset>
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-700">Skills</p>
          <SkillChips
            skills={values.skills}
            onChange={(skills) => set('skills', skills)}
          />
          <FieldError message={problems.skills} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            Seniority
            <select
              value={values.seniority}
              onChange={(e) => set('seniority', e.target.value)}
              className={`${INPUT} w-48`}
            >
              {SENIORITIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <span className="text-[11px] font-normal text-gray-500">
            Senior, lead and manager roles aren&apos;t accepted: graduates never
            get them.
          </span>
          <FieldError message={problems.seniority} />
        </div>
        <div className="flex flex-col gap-1">
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
            One-line summary for the email
            <input
              type="text"
              maxLength={MAX_SUMMARY}
              value={values.summary}
              onChange={(e) => set('summary', e.target.value)}
              className={INPUT}
            />
          </label>
          <span className="text-[11px] font-normal text-gray-500">
            {values.summary.length}/{MAX_SUMMARY}
          </span>
          <FieldError message={problems.summary} />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={SECONDARY}>
          Cancel
        </button>
        <button
          type="submit"
          disabled={!ready || save.isLoading}
          className={PRIMARY}
        >
          {save.isLoading ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
